/* eslint no-console: 0 */

import get from 'lodash/get';
import {LOCATION_CHANGE} from 'react-router-redux';

import modal from '../modules/modal/types';
import user from '../modules/user/types';
import loads from '../modules/loads/types';
import search from '../modules/loadsSearch/types';
import dropdowns from '../modules/dropdowns/types';
import tabs from '../modules/tabs/types';
import backhaulT from '~/modules/backhaul/types';
import windowT from '~/modules/window/types';
import {getLoad, getLoadEntry} from '~/misc/metrics/utils';
import {track, trackPageView, setUserProps} from '~/misc/metrics';
import accounting from '~/misc/accounting';

// don't forget to change/add event version/id if you change/add modal version
const contextToEventVersion = {
  premium_loads: 'premium_1.1',
};

const requestingEvents = {
  [search.LOADS_MORE]: () => ['request_more_loads'],
  [search.LOADS_SORT]: () => ['request_sort_loads'],
  [search.LOADS_SEARCH]: () => ['request_search_loads'],
};

const isDropDownChanged = (id, prev, next) => Boolean(prev.dropdowns[id]) !== Boolean(next.dropdowns[id]);

const poi = (state, toggledState, name, loadIdentity) => {
  const id = loadIdentity.props.id;
  const load = getLoad(id, state, loadIdentity);

  return [name, {
    ...load,
    expanded: Boolean(toggledState[id]),
  }];
};

const events = {
  [loads.LOADS_TOGGLED_ROUTE_TRUCK_STOPS]: (action, prev, next) =>
    poi(next, next.loads.toggledTruckStops, 'poi_truckstops_toggled', action.payload),
  [loads.LOADS_TOGGLED_ROUTE_WEIGHT_STATIONS]: (action, prev, next) =>
    poi(next, next.loads.toggledTruckStops, 'poi_weightstations_toggled', action.payload),
  [loads.LOADS_TOGGLED_ROUTE_FUEL_PRICES]: (action, prev, next) =>
    poi(next, next.loads.toggledTruckStops, 'poi_fuelprices_toggled', action.payload),
  [backhaulT.BACKHAUL_TOGGLED]: (action, prev, next) => {
    const loadId = action.payload;
    const load = getLoad(loadId, next); // TODO pass loadIdentity to `getLoad`
    const backhaul = next.backhaul.index[loadId];

    return ['backhaul_toggled', {
      load,
      originalQuery: backhaul.originanFormQuery,
      expanded: next.backhaul.active[load.idMediumkey],
    }];
  },
  [backhaulT.BACKHAUL_LANE_VIEWED]: (action, prev, next) => {
    const {parentIdentity} = action.payload;
    const backhaul = next.backhaul.index[parentIdentity.key];
    const lane = backhaul.index[action.payload.shortkey];

    const loadsCount = get(
      next.resources,
      ['byName', [lane.loadsIdentity.name], [lane.loadsIdentity.key], 'item', 'total_shipments']
    );

    return ['backhaul_lane_opened', {
      loadsCount,
      ...action.payload.props,
      load: getLoad(parentIdentity.props.loadId, next, parentIdentity.props.loadIdentity),
      originalQuery: backhaul.originanFormQuery,
      query: lane.formQuery,
    }];
  },
  [backhaulT.BACKHAUL_BOOKINGLOAD_REVEALED]: ({payload}, prev, next) => ['backhaul_booknow_load_preview', {
    bookingLoad: getLoad(payload.bookingLoad.props.id, next, payload.bookingLoad),
    expandedLoad: getLoad(payload.expandedLoad.props.id, next, payload.expandedLoad),
  }],
  [backhaulT.BACKHAUL_BOOKINGLOAD_OPENED]: ({payload}, prev, next) => {
    const backhaul = prev.backhaul.index[payload.key];
    const expandedLoad = backhaul.identity.props.loadIdentity;
    const bookingLoad = get(backhaul, 'bookingLoad.identity');

    return ['backhaul_booknow_load_open', {
      bookingLoad: bookingLoad && getLoad(bookingLoad.props.id, next, bookingLoad),
      expandedLoad: expandedLoad && getLoad(expandedLoad.props.id, next, expandedLoad),
    }];
  },
  [modal.MODAL_URI_CHANGED]: (action, prev, next) => ['view_modal', {
    screen: action.payload.uri,
    action: get(next, 'modal.context'),
    from_modal: action.payload.context === true,
    version: contextToEventVersion[get(next, 'modal.context')],
  }],

  [user.LOGIN]: (action, prev, next) => ['login', {
    action: get(next, 'modal.context'),
    version: contextToEventVersion[get(next, 'modal.context')],
  }],
  [user.SIGNUP]: (action, prev, next) => ['signup', {
    action: get(next, 'modal.context'),
    version: contextToEventVersion[get(next, 'modal.context')],
  }],
  [user.SIGNUPV2]: (action, prev, next) => ['signup', {
    Outcome: action.payload ? action.payload.message ? 'Validation Error' : 'Success' : 'Success',
    action: get(next, 'modal.context'),
    version: contextToEventVersion[get(next, 'modal.context')],
    ...action.payload.errors,
  }],
  [user.LOGOUT]: () => ['logout'],
  [user.RESET_PASSWORD]: (action, prev, next) => ['reset_password', {
    action: get(next, 'modal.context'),
    version: contextToEventVersion[get(next, 'modal.context')],
  }],

  [search.LOADS_SORT]: (action, prev, next) => ['loads_sort', {
    by: get(next, 'loadsSearch.query.sort_by'),
    order: get(next, 'loadsSearch.query.sort_order'),
  }],
  [loads.LOADS_SKIP_BOOKNOW]: (action, prev) => {
    const id = action.payload.props.id;
    const load = getLoad(id, prev, action.payload);

    setTimeout(() => track('skip_booknow_load', {load}), 100);
    /*
      should not accure before 'loads_toggle'/SEARCH_RESULTS_TOGGLE
    */
  },
  [loads.SEARCH_RESULTS_TOGGLE]: (action, prev, next) => {
    const id = action.payload.props.id;

    const loadEntry = getLoadEntry(id, prev, action.payload);

    const load = getLoad(id, prev, action.payload);

    const expanded = Boolean(next.loads.active[load.idMediumkey]);

    const searchId = get(prev, "loadsSearch.results.searchId");

    if (expanded && loadEntry && loadEntry.book_now) {
      const priceTitle = loadEntry.target_price ? ` for ${accounting.formatMoney(loadEntry.target_price)}` : '';
      setUserProps({
        last_booknow_load: `from ${loadEntry.from} to ${loadEntry.to} by ${loadEntry.company}${priceTitle}`,
      });
    }

    return ['loads_toggle', {
      ...load,
      load,
      expanded,
      viewed_before: Boolean(prev.loads.viewed[id]),
      searchId
    }];
  },

  [loads.TOGGLE_LOADPHONE]: (action, prev, next) => {
    const id = action.payload.props.id;
    const load = getLoad(id, prev, action.payload);

    return ['show_phone', {
      ...load,
      load,
      map_shown: Boolean(prev.loads.maps[id]),
      expanded: Boolean(next.loads.phoneToggled[id]),
    }];
  },

  [dropdowns.TOGGLE_DROPDOWN]: ({payload: id}, prev, next) => ['toggle_dropdown', {
    id,
    expanded: Boolean(next.dropdowns[id]),
  }],
  [dropdowns.CLOSE_DROPDOWN]: ({payload: id}, prev, next) => (isDropDownChanged(id, prev, next) && ['close_dropdown', {
    id,
  }]),
  [dropdowns.OPEN_DROPDOWN]: ({payload: id}, prev, next) => (isDropDownChanged(id, prev, next) && ['open_dropdown', {
    id,
  }]),

  [tabs.SELECT_TAB]: ({id, selectedTab}) => ['select_tab', {
    panel: id,
    selected: selectedTab,
  }],

  [windowT.WINDOW_FOCUSED]: () => ['window_focused'],
  [windowT.WINDOW_BLURRED]: () => ['window_blurred'],
};

const metricsLogger = (store) => nextDispatch => action => {
  if (action.type === LOCATION_CHANGE) {
    nextDispatch(action);

    try {
      trackPageView(action);
    } catch (err) {
      console.error(err);
    }
    return;
  }

  if (action.error
      || (!events.hasOwnProperty(action.type)
          && (!action.pending || !requestingEvents.hasOwnProperty(action.type)))) {
    nextDispatch(action);
    return;
  }


  const prevState = store.getState();
  nextDispatch(action);
  const newState = store.getState();

  try {
    const data = action.pending
      ? (requestingEvents[action.type] && requestingEvents[action.type](action, prevState, newState))
      : events[action.type](action, prevState, newState);

    if (data) {
      track(...data);
    }
  } catch (err) {
    console.error(err)
  }
};

export default metricsLogger;



// WEBPACK FOOTER //
// ./src/middlewares/metrics.js